/**
 * usePushRegistration — native FCM token registration for the Capacitor shell.
 *
 * Uses @capacitor-firebase/messaging (which transitively pulls in the
 * Firebase iOS SDK on iOS and Firebase Messaging on Android) so we receive
 * the SAME FCM registration token shape on both platforms. The token is
 * upserted to the existing POST /api/notifications/device-token endpoint.
 *
 * Behaviour:
 *  - No-op on the web (returns immediately).
 *  - On iOS / Android, registers the device REGARDLESS of sign-in state, so a
 *    person who downloaded the app and never signed up — and a person who
 *    deleted their account but kept the app — still receives admin broadcasts.
 *    An anonymous registration stores `userId = null` server-side; signing in
 *    later re-posts the same token with an Authorization header and the server
 *    rebinds that same row via ON CONFLICT. See server/routes/notifications.ts.
 *  - Re-registers on token refresh (Firebase rotates the token periodically)
 *    via the `tokenReceived` listener, and whenever the signed-in identity
 *    changes, so a device that switches accounts always reflects the latest
 *    user.
 *  - Foreground arrivals are logged but not toasted — the existing WebSocket
 *    flow already toasts/invalidates caches in parallel for the open-app
 *    case, and double-firing would be confusing.
 *  - Tapping a notification (background / killed) deep-links to the relevant
 *    screen via `notificationActionPerformed` + `resolveNotificationRoute`.
 *
 * Permission is asked for behind our own explainer rather than firing the OS
 * dialog on launch. The system prompt can be accepted or refused exactly once
 * per install on iOS — once refused, only a trip to Settings can undo it — so
 * spending it on a cold first second, before the person has seen a single
 * product, throws away the only chance we get. The hook surfaces
 * `explainerOpen` plus two handlers; App.tsx renders PushPermissionDialog from
 * them. Dismissing the explainer does NOT consume the OS prompt, so we can ask
 * again after a cooling-off period.
 *
 * What this hook does NOT do (deferred):
 *  - No de-registration on logout — the server's upsert already rebinds the
 *    token to whoever logs in next on the same device, which covers the
 *    account-switch case.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import {
  FirebaseMessaging,
  type Notification as FirebaseMessagingNotification,
} from "@capacitor-firebase/messaging";
import { useAuth } from "./use-auth";
import { buildApiUrl, getAuthToken } from "@/lib/apiBase";

/** Wait this long after the app settles before showing the explainer. */
const EXPLAINER_DELAY_MS = 2500;
/** How long a "not now" is honoured before we offer again. */
const EXPLAINER_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const EXPLAINER_SNOOZE_KEY = "iraqsooq_push_explainer_snoozed_at";

/**
 * Map a tapped notification's data payload to an in-app route. Each server
 * notification type is delivered to a single recipient role, so the type
 * alone is enough to pick the destination — no role guessing needed. Returns
 * `null` for types with no useful deep-link target (the app just opens
 * normally). The server flattens all `data` fields to strings (see
 * server/ws.ts fireNativePush), so `type`, `conversationId`, etc. arrive here.
 */
function resolveNotificationRoute(
  data: Record<string, string> | undefined | null,
): string | null {
  if (!data) return null;
  const type = data.type ?? "";
  const conv = data.conversationId;
  switch (type) {
    case "new_message":
    case "new_customer_message":
      return conv ? `/messages?conv=${conv}` : "/messages";
    // Order just landed for the store owner.
    case "new_order":
    case "new_store_own_order":
      return "/virtual-shop/orders";
    // Merchant must prepare an accepted dropshipping order.
    case "order_to_prepare":
      return "/merchant/dropshipping-orders";
    case "new_direct_wholesale_order":
      return "/merchant/direct-wholesale-orders";
    case "new_return":
      return "/returns/incoming";
    case "return_decision":
      return "/returns";
    case "payout_complete":
    case "payout_approved":
    case "payout_hold":
    case "payout_release":
    case "credit":
    case "fee":
      return "/wallet";
    // Buyer-facing order lifecycle updates.
    case "order_update":
    case "order_confirmed":
    case "order_shipped":
    case "order_delivered":
    case "order_accepted":
    case "order_rejected":
    case "order_cancelled":
    case "order_auto_cancelled":
    case "order_placed":
    case "delivery_otp":
    case "delivery_failed":
      return "/my-orders";
    default:
      return null;
  }
}

/**
 * Auth headers are best-effort: an anonymous device simply posts without one
 * and the server stores a null user id. Never throws.
 */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function postTokenToServer(fcmToken: string): Promise<boolean> {
  const platform = Capacitor.getPlatform() === "ios" ? "ios" : "android";
  try {
    const res = await fetch(buildApiUrl("/api/notifications/device-token"), {
      method: "POST",
      credentials: "include",
      headers: authHeaders(),
      body: JSON.stringify({
        platform,
        token: fcmToken,
        deviceMetadata: { capacitorPlatform: Capacitor.getPlatform() },
      }),
    });
    if (!res.ok) {
      console.error("[push-reg] server rejected token, status:", res.status);
      return false;
    }
    console.log("[push-reg] FCM token registered with server");
    return true;
  } catch (err) {
    console.error("[push-reg] register fetch failed:", (err as Error).message);
    return false;
  }
}

/** Storage is best-effort — a locked-down WebView must not break registration. */
function readSnoozedAt(): number {
  try {
    const raw = window.localStorage.getItem(EXPLAINER_SNOOZE_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeSnoozedAt(at: number): void {
  try {
    window.localStorage.setItem(EXPLAINER_SNOOZE_KEY, String(at));
  } catch {
    /* private mode / storage disabled — we just ask again next launch */
  }
}

export interface PushRegistrationState {
  /** True while our own explainer should be on screen. */
  explainerOpen: boolean;
  /** User tapped "enable" — fires the OS prompt, then registers if granted. */
  onEnable: () => void;
  /** User tapped "not now" — snoozes without consuming the OS prompt. */
  onDismiss: () => void;
}

export function usePushRegistration(): PushRegistrationState {
  const { isAuthenticated, isInitialized, user } = useAuth();
  // Identity of the last successful registration, as `${userId}::${token}`.
  // Keyed on both halves so signing in re-posts the same token and lets the
  // server rebind the row from anonymous to this user.
  const lastRegisteredRef = useRef<string | null>(null);
  const [explainerOpen, setExplainerOpen] = useState(false);
  // wouter navigate, kept in a ref so the long-lived FCM tap listener always
  // calls the latest client-side navigation function (no full page reload).
  const [, navigate] = useLocation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Read inside callbacks without making them depend on the value.
  const identityRef = useRef<string>("anon");
  identityRef.current = isAuthenticated && user?.id ? String(user.id) : "anon";

  /** Fetch the current FCM token and push it up if it (or the user) changed. */
  const registerCurrentToken = useCallback(async () => {
    let token: string | null = null;
    try {
      const result = await FirebaseMessaging.getToken();
      token = result.token ?? null;
    } catch (err) {
      // Common during the first-launch race on iOS — the tokenReceived
      // listener below delivers it a moment later. Not fatal.
      console.warn("[push-reg] getToken failed:", (err as Error).message);
      return;
    }
    if (!token) return;
    const key = `${identityRef.current}::${token}`;
    if (key === lastRegisteredRef.current) return;
    lastRegisteredRef.current = key;
    const ok = await postTokenToServer(token);
    if (!ok) lastRegisteredRef.current = null;
  }, []);

  // ── Listeners: attached once permission is granted ───────────────────────
  const listenersBoundRef = useRef(false);
  const bindListeners = useCallback(async () => {
    if (listenersBoundRef.current) return;
    listenersBoundRef.current = true;

    // Token refresh (Firebase rotates tokens periodically).
    await FirebaseMessaging.addListener("tokenReceived", async (event) => {
      const refreshed = event?.token;
      if (!refreshed) return;
      const key = `${identityRef.current}::${refreshed}`;
      if (key === lastRegisteredRef.current) return;
      lastRegisteredRef.current = key;
      const ok = await postTokenToServer(refreshed);
      if (!ok) lastRegisteredRef.current = null;
    });

    // Foreground arrival — logged only. The WebSocket flow already handles
    // the open-app toast and cache invalidation.
    await FirebaseMessaging.addListener("notificationReceived", (event: {
      notification: FirebaseMessagingNotification;
    }) => {
      console.log("[push-reg] foreground push received:", event?.notification?.title);
    });

    // Notification tap (background / killed) → deep-link. Also delivers the
    // tap that cold-launched the app, so deep-linking works from a killed
    // state too.
    await FirebaseMessaging.addListener("notificationActionPerformed", (event: {
      notification: FirebaseMessagingNotification;
    }) => {
      const data = (event?.notification?.data ?? null) as Record<string, string> | null;
      const route = resolveNotificationRoute(data);
      if (!route) return;
      try {
        navigateRef.current(route);
      } catch (err) {
        console.warn("[push-reg] deep-link navigation failed:", (err as Error).message);
      }
    });
  }, []);

  // ── Main effect ──────────────────────────────────────────────────────────
  // Runs for signed-in and anonymous devices alike. Re-runs when the identity
  // settles or changes so an already-granted device re-posts its token and the
  // server rebinds the row.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Wait for the auth query to settle so the first registration carries the
    // right identity instead of landing anonymously and needing a rebind.
    if (!isInitialized) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function boot() {
      let status: string;
      try {
        status = (await FirebaseMessaging.checkPermissions()).receive;
      } catch (err) {
        console.error("[push-reg] checkPermissions failed:", (err as Error).message);
        return;
      }
      if (cancelled) return;

      if (status === "granted") {
        await bindListeners();
        if (!cancelled) await registerCurrentToken();
        return;
      }

      // Denied is terminal until the person changes it in system Settings —
      // the OS will not show the prompt again, so there is nothing to ask.
      if (status !== "prompt" && status !== "prompt-with-rationale") return;

      // Still askable. Offer the explainer unless a recent "not now" stands.
      const snoozedAt = readSnoozedAt();
      if (snoozedAt && Date.now() - snoozedAt < EXPLAINER_SNOOZE_MS) return;
      timer = setTimeout(() => {
        if (!cancelled) setExplainerOpen(true);
      }, EXPLAINER_DELAY_MS);
    }

    void boot();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isInitialized, isAuthenticated, user?.id, bindListeners, registerCurrentToken]);

  const onEnable = useCallback(() => {
    setExplainerOpen(false);
    void (async () => {
      try {
        const requested = await FirebaseMessaging.requestPermissions();
        if (requested.receive !== "granted") {
          console.log("[push-reg] permission not granted:", requested.receive);
          return;
        }
        await bindListeners();
        await registerCurrentToken();
      } catch (err) {
        console.error("[push-reg] permission request failed:", (err as Error).message);
      }
    })();
  }, [bindListeners, registerCurrentToken]);

  const onDismiss = useCallback(() => {
    setExplainerOpen(false);
    // Snooze only — the OS prompt is untouched, so we may offer again later.
    writeSnoozedAt(Date.now());
  }, []);

  return { explainerOpen, onEnable, onDismiss };
}
